import React, { useRef, useEffect } from 'react';
import { FiChevronDown, FiChevronRight, FiMoreVertical } from 'react-icons/fi';
import { type ScheduleItem, type ScheduleQuestion, type ScheduleOccurrence } from '../../../utils/scheduleTransform';

export interface ScheduleItemRowProps {
  item: ScheduleItem;
  readIds: Set<string>;
  expandedIds: string[];
  expandedQuestionIds: string[];
  selectedOccurrenceId: string | null;
  openMenuId: string | null;
  onToggleExpand: (id: string) => void;
  onToggleExpandQuestion: (id: string) => void;
  onMarkRead: (occurrenceId: string) => void;
  onMarkUnread: (occurrenceId: string) => void;
  onOccurrenceSelect: (item: ScheduleItem, question: ScheduleQuestion, occurrence: ScheduleOccurrence) => void;
  onSetOpenMenuId: (id: string | null) => void;
}

const ScheduleItemRow: React.FC<ScheduleItemRowProps> = ({
  item,
  readIds,
  expandedIds,
  expandedQuestionIds,
  selectedOccurrenceId,
  openMenuId,
  onToggleExpand,
  onToggleExpandQuestion,
  onMarkRead,
  onMarkUnread,
  onOccurrenceSelect,
  onSetOpenMenuId,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onSetOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSetOpenMenuId]);
  const isExpanded = expandedIds.includes(item.id);
  const itemUnreadCount = item.questions.reduce(
    (acc, q) => acc + q.occurrences.filter((o) => !readIds.has(o.id)).length,
    0
  );

  // Surface questions with unread occurrences first; occurrences stay newest-first.
  const sortedQuestions = [...item.questions].sort((a, b) => {
    const aUnread = a.occurrences.some((o) => !readIds.has(o.id));
    const bUnread = b.occurrences.some((o) => !readIds.has(o.id));
    if (aUnread === bUnread) return 0;
    return aUnread ? -1 : 1;
  });

  return (
    <div>
      {/* Schedule title row */}
      <div
        onClick={() => onToggleExpand(item.id)}
        className="group flex cursor-pointer items-center gap-2 rounded-eai-card px-2.5 py-2 transition-colors hover:bg-eai-surface-sunken active:bg-eai-surface-hover"
      >
        <span className="flex-shrink-0 text-eai-ink-faint">
          {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        </span>
        <span className="eai-card-title flex-1 truncate font-medium">
          {item.title}
        </span>
        {itemUnreadCount > 0 && (
          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-eai-caption font-bold bg-eai-cta-primary text-white">
            {itemUnreadCount}
          </span>
        )}
        <span className="eai-tag-pill-sm flex-none px-1.5 py-0 text-eai-caption">
          {item.runtime.split(' ')[0]}
        </span>
      </div>

      {/* Expanded questions */}
      {isExpanded && (
        <div className="ml-4 mt-1 mb-1 space-y-0.5 border-l-2 border-eai-checklist-lt pl-2">
          {sortedQuestions.map((question) => {
            const isQuestionExpanded = expandedQuestionIds.includes(question.id);
            const questionUnreadCount = question.occurrences.filter((o) => !readIds.has(o.id)).length;

            return (
              <div key={question.id}>
                {/* Question row */}
                <div
                  onClick={() => onToggleExpandQuestion(question.id)}
                  className="group flex cursor-pointer items-center gap-1.5 rounded-eai-card px-2.5 py-1.5 font-inter text-eai-body-sm text-eai-ink-soft transition-colors hover:bg-eai-surface-sunken hover:text-eai-ink active:bg-eai-surface-hover active:text-eai-ink"
                >
                  <span className="flex-shrink-0 text-eai-ink-faint">
                    {isQuestionExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                  </span>
                  <span className="flex-1 truncate">{question.question}</span>
                  {questionUnreadCount > 0 && (
                    <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-eai-caption font-bold bg-eai-cta-primary text-white">
                      {questionUnreadCount}
                    </span>
                  )}
                </div>

                {/* Expanded dates */}
                {isQuestionExpanded && (
                  <div className="ml-3 mt-0.5 mb-0.5 space-y-0.5 border-l-2 border-eai-border pl-2">
                    {question.occurrences.map((occurrence) => {
                      const isUnread = !readIds.has(occurrence.id);
                      const isMenuOpen = openMenuId === occurrence.id;

                      return (
                        <div
                          key={occurrence.id}
                          className={`group relative flex cursor-pointer items-center gap-1.5 rounded-eai-card px-2.5 py-1.5 font-inter text-eai-body-xs transition-colors ${
                            selectedOccurrenceId === occurrence.id
                              ? 'bg-eai-checklist-lt text-eai-ink'
                              : 'text-eai-ink-muted hover:bg-eai-checklist-lt hover:text-eai-ink active:bg-eai-checklist-lt active:text-eai-ink'
                          }`}
                          onClick={() => {
                            onMarkRead(occurrence.id);
                            onOccurrenceSelect(item, question, occurrence);
                          }}
                        >
                          {occurrence.status === 'limit_exceeded' ? (
                            <span
                              className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-eai-warning-text"
                              title="Skipped — monthly token limit reached"
                            />
                          ) : occurrence.error ? (
                            <span
                              className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-eai-danger-solid"
                              title="This run failed"
                            />
                          ) : isUnread ? (
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-eai-cta-primary" />
                          ) : null}
                          <span className="truncate flex-1">{occurrence.title}</span>

                          {/* Condition verdict badge — only on condition-gated runs. Tooltip carries the judge's reason; "email sent anyway" covers notify-mode and fail-open. */}
                          {occurrence.verdict && (
                            occurrence.verdict.met === true ? (
                              <span
                                className={`flex-shrink-0 inline-flex items-center h-[16px] px-1.5 rounded-full text-eai-caption font-medium ${
                                  occurrence.verdict.emailSent ? 'bg-eai-cta-primary text-white' : 'bg-eai-brand-peach text-eai-warning-text'
                                }`}
                                title={`${occurrence.verdict.reason || 'Condition met'}${occurrence.verdict.emailSent ? '' : ' — email failed to send'}`}
                              >
                                ✉ alert
                              </span>
                            ) : occurrence.verdict.met === false ? (
                              <span
                                className="flex-shrink-0 inline-flex items-center h-[16px] px-1.5 rounded-full text-eai-caption font-medium bg-eai-surface-hover text-eai-ink-subtle"
                                title={`${occurrence.verdict.reason || 'Condition not met'}${occurrence.verdict.emailSent ? ' — email sent anyway' : ''}`}
                              >
                                ○ not met
                              </span>
                            ) : (
                              <span
                                className="flex-shrink-0 inline-flex items-center h-[16px] px-1.5 rounded-full text-eai-caption font-medium bg-eai-brand-peach text-eai-warning-text"
                                title={`Condition couldn't be evaluated${occurrence.verdict.emailSent ? ' — email sent anyway' : ''}`}
                              >
                                ⚠
                              </span>
                            )
                          )}

                          {/* Kebab button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSetOpenMenuId(isMenuOpen ? null : occurrence.id);
                            }}
                            className="flex-shrink-0 opacity-30 group-hover:opacity-100 group-active:opacity-100 p-0.5 rounded-full hover:bg-eai-surface-active active:bg-eai-surface-active transition-opacity"
                          >
                            <FiMoreVertical size={12} />
                          </button>

                          {/* Dropdown menu */}
                          {isMenuOpen && (
                            <div
                              ref={menuRef}
                              className="eai-card absolute right-0 top-full z-10 mt-1 min-w-[160px] p-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isUnread ? (
                                <button
                                  onClick={() => { onMarkRead(occurrence.id); onSetOpenMenuId(null); }}
                                  className="eai-menu-item flex w-full items-center gap-2 whitespace-nowrap text-left"
                                >
                                  Mark as read
                                </button>
                              ) : (
                                <button
                                  onClick={() => { onMarkUnread(occurrence.id); onSetOpenMenuId(null); }}
                                  className="eai-menu-item flex w-full items-center gap-2 whitespace-nowrap text-left"
                                >
                                  Mark as unread
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScheduleItemRow;
